import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, distinctUntilChanged, filter } from 'rxjs';
import { ChartService } from '../../core/services/chart/chart.service';
import { FilterStateService, StatisticsGranularity } from '../../core/services/filter-state/filter-state.service';
import { StructureLabelsService } from '../../core/services/structure/structure-labels.service';
import { StructureService } from '../../core/services/structure/structure.service';
import { ProjectStructureLevel } from '../../core/models/project-structure.model';
import { TranslocoService } from '@jsverse/transloco';
import { ProjectContextService } from '../../core/services/project-context/project-context.service';
import { SYSTEM_CONTENT_TYPE_METRICS } from '../../core/constants/system-content-types.constant';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit, OnDestroy {
  public year: number | undefined;
  public month: number | undefined;
  public day: number | undefined;
  public granularity: StatisticsGranularity = null;
  public loadingAvailability = false;
  public availabilityError: string | null = null;
  public years: number[] = [];
  public months: number[] = [];
  public days: number[] = [];
  public topLevelContentTypes: string[] = [];
  public readonly systemTypeViews = [...SYSTEM_CONTENT_TYPE_METRICS.keys()];
  public activeLanguage = 'es';

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly router: Router,
    private readonly chartService: ChartService,
    private readonly filterStateService: FilterStateService,
    private readonly structureService: StructureService,
    private readonly structureLabelsService: StructureLabelsService,
    private readonly translocoService: TranslocoService,
    private readonly projectContext: ProjectContextService
  ) { }

  ngOnInit(): void {
    this.activeLanguage = this.translocoService.getActiveLang();

    this.subscriptions.add(
      this.translocoService.langChanges$.subscribe((lang) => {
        this.activeLanguage = lang;
      })
    );

    this.subscriptions.add(
      this.filterStateService.state$.subscribe((state) => {
        this.year = state.selectedYear ?? undefined;
        this.month = state.selectedMonth ?? undefined;
        this.day = state.selectedDay ?? undefined;
        this.granularity = state.granularity;
        this.loadingAvailability = state.loadingAvailability;
        this.availabilityError = state.availabilityError;
        this.years = state.availability?.years.map((entry) => entry.year) ?? [];

        if (state.selectedYear === null || state.availability === null) {
          this.months = [];
          this.days = [];
          return;
        }

        const selectedYearEntry = state.availability.years.find((entry) => entry.year === state.selectedYear);
        this.months = selectedYearEntry?.months.map((entry) => entry.month) ?? [];

        if (state.selectedMonth === null) {
          this.days = [];
          return;
        }

        const selectedMonthEntry = selectedYearEntry?.months.find((entry) => entry.month === state.selectedMonth);
        this.days = selectedMonthEntry?.days ?? [];
      })
    );

    this.subscriptions.add(
      this.projectContext.selectedProjectId$
        .pipe(
          filter((id): id is string => typeof id === 'string' && id.trim().length > 0),
          distinctUntilChanged()
        )
        .subscribe((projectId) => {
          this.subscriptions.add(
            this.structureService.getProjectStructure(projectId).subscribe({
              next: (structure) => {
                this.topLevelContentTypes = this.extractTopLevelContentTypes(structure.levels);
              },
              error: () => {
                this.topLevelContentTypes = [];
              }
            })
          );

          this.subscriptions.add(
            this.filterStateService.loadAvailability(projectId).subscribe({
              error: () => {
                // The error state is already stored in FilterStateService.
              }
            })
          );
        })
    );
  }

  selectYear(year: number): void {
    this.filterStateService.selectYear(year);
  }

  selectMonth(month: number): void {
    this.filterStateService.selectMonth(month);
  }

  selectDay(day: number): void {
    this.filterStateService.selectDay(day);
  }

  clearDaySelection(): void {
    this.filterStateService.selectDay(null);
  }



  canNavigate(): boolean {
    return typeof this.year === 'number' && typeof this.month === 'number';
  }

  goTo(path: string): void {
    if (!this.canNavigate() || typeof this.year !== 'number' || typeof this.month !== 'number') {
      return;
    }

    this.chartService.getDataByYear(this.year, this.month, this.day ?? null);
    this.router.navigate([`/${path}`]);
  }

  goToChartsByContentType(contentTypeId: string): void {
    if (!this.canNavigate() || typeof this.year !== 'number' || typeof this.month !== 'number') {
      return;
    }

    this.chartService.getDataByYear(this.year, this.month, this.day ?? null);
    this.router.navigate([`/${contentTypeId}`]);
  }

  getStructureTypeLabel(contentTypeId: string): string {
    return this.structureLabelsService.getLabel(contentTypeId, this.activeLanguage);
  }

  private extractTopLevelContentTypes(levels: ProjectStructureLevel[]): string[] {
    if (!Array.isArray(levels) || levels.length === 0) {
      return [];
    }

    const minLevel = Math.min(...levels.map((level) => Number(level.level)));
    return levels
      .filter((level) => Number(level.level) === minLevel)
      .map((level) => level.contentTypeId)
      .filter((value, index, array) => array.indexOf(value) === index);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
